import { useForm } from "@inertiajs/react";
import PageHeader from "@/components/PageHeader";
import Button from "@/components/buttons/Button";
import Input from "@/components/inputs/Input";
import Select, { SelectOption } from "@/components/inputs/Select";
import FilterableSelect from "@/components/inputs/FilterableSelect";
import TagInput from "@/components/inputs/TagInput";
import TextArea from "@/components/inputs/TextArea";
import Checkbox from "@/components/inputs/Checkbox";
import InertiaLink from "@/components/buttons/InertiaLink";
import { Artwork } from "@/components/ArtworkSlot";
import { Track } from "./Show";
import FormActions from "@/components/buttons/FormActions";

interface EditProps {
  track: Track;
  artwork: Artwork;
  artist_options: SelectOption[];
  song_key_options: SelectOption[];
  genre_options: SelectOption[];
}

const Edit: React.FC<EditProps> = ({
  track,
  artwork,
  artist_options,
  song_key_options,
  genre_options,
}) => {
  const { data, setData, put, processing, errors } = useForm({
    artist_id: track.artist_id.toString(),
    title: track.title,
    description: track.description || "",
    explicit: track.explicit,
    primary_genre: track.primary_genre || "",
    secondary_genre: track.secondary_genre || "",
    isrc_code: track.isrc_code || "",
    upc_code: track.upc_code || "",
    release_date: track.release_date || "",
    themes: track.themes || "",
    moods: track.moods || "",
    song_key: track.song_key || "",
    tempo_bpm: track.tempo_bpm?.toString() || "",
    time_signature: track.time_signature || "",
    length: track.formatted_length || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    put(`/tracks/${track.id}`);
  };

  return (
    <div className="flex w-full flex-col items-start justify-start gap-5 md:gap-10">
      <PageHeader
        backLink={{ href: `/tracks/${track.id}`, text: "Back to details" }}
        title={`Edit ${track.title}`}
        secondaryAction={{ href: `/tracks/${track.id}`, text: "Cancel" }}
        artwork={{
          trackId: track.id,
          uploaded: track.artwork_attached,
          thumbnailUrl: artwork.thumbnail_url,
          originalUrl: artwork.original_url,
          alt: `${track.title} artwork`,
        }}
      />
      <form
        onSubmit={handleSubmit}
        className="dark:bg-cream-950 flex w-full flex-col gap-6 rounded-2xl bg-white p-6 shadow md:p-10"
      >
        <Select
          label="Artist *"
          name="artist_id"
          placeholder="Select an artist"
          options={artist_options}
          value={data.artist_id}
          onChange={(e) => setData("artist_id", e.target.value)}
          errors={errors.artist_id}
        />

        <Input
          type="text"
          label="Title *"
          name="title"
          placeholder="Enter track title"
          value={data.title}
          onChange={(e) => setData("title", e.target.value)}
          errors={errors.title}
        />

        <FilterableSelect
          label="Primary Genre"
          name="primary_genre"
          placeholder="Select a genre"
          options={genre_options}
          value={data.primary_genre}
          onChange={(value) => setData("primary_genre", value)}
          errors={errors.primary_genre}
        />

        <FilterableSelect
          label="Secondary Genre"
          name="secondary_genre"
          placeholder="Select a genre"
          options={genre_options}
          value={data.secondary_genre}
          onChange={(value) => setData("secondary_genre", value)}
          errors={errors.secondary_genre}
        />

        <Input
          type="text"
          label="ISRC Code"
          name="isrc_code"
          placeholder="e.g. USXXX0000000"
          value={data.isrc_code}
          onChange={(e) => setData("isrc_code", e.target.value)}
          errors={errors.isrc_code}
        />

        <Input
          type="text"
          label="UPC Code"
          name="upc_code"
          placeholder="Enter UPC code"
          value={data.upc_code}
          onChange={(e) => setData("upc_code", e.target.value)}
          errors={errors.upc_code}
        />

        <Input
          type="date"
          label="Release Date"
          name="release_date"
          value={data.release_date}
          onChange={(e) => setData("release_date", e.target.value)}
          errors={errors.release_date}
        />

        <TagInput
          label="Themes"
          name="themes"
          placeholder="e.g. Love, Coming of Age, Nostalgia"
          value={data.themes}
          onChange={(e) => setData("themes", e.target.value)}
          errors={errors.themes}
        />

        <TagInput
          label="Moods"
          name="moods"
          placeholder="e.g. Upbeat, Late Night Drive, Energetic"
          value={data.moods}
          onChange={(e) => setData("moods", e.target.value)}
          errors={errors.moods}
        />

        <Select
          label="Song Key"
          name="song_key"
          placeholder="Select a key"
          options={song_key_options}
          value={data.song_key}
          onChange={(e) => setData("song_key", e.target.value)}
          errors={errors.song_key}
        />

        <Input
          type="text"
          label="Tempo (BPM)"
          name="tempo_bpm"
          placeholder="e.g. 120"
          value={data.tempo_bpm}
          onChange={(e) => setData("tempo_bpm", e.target.value)}
          errors={errors.tempo_bpm}
        />

        <Input
          type="text"
          label="Time Signature"
          name="time_signature"
          placeholder="e.g. 4/4, 3/4"
          value={data.time_signature}
          onChange={(e) => setData("time_signature", e.target.value)}
          errors={errors.time_signature}
        />

        <Input
          type="text"
          label="Length"
          name="length"
          placeholder="e.g. 3:45"
          value={data.length}
          onChange={(e) => setData("length", e.target.value)}
          errors={errors.length}
        />

        <TextArea
          label="Description"
          name="description"
          placeholder="A short description of the track"
          value={data.description}
          onChange={(e) => setData("description", e.target.value)}
          errors={errors.description}
        />

        <Checkbox
          label="Content rating"
          checkboxLabel="Explicit"
          name="explicit"
          checked={data.explicit}
          onChange={(e) => setData("explicit", e.target.checked)}
          errors={errors.explicit}
        />

        <FormActions>
          <InertiaLink
            href={`/tracks/${track.id}`}
            type="secondary"
            text="Cancel"
          />
          <Button
            text={processing ? "Saving..." : "Save"}
            type="primary"
            htmlButtonType="submit"
            disabled={processing}
          />
        </FormActions>
      </form>
    </div>
  );
};

export default Edit;
